/**
 * ExportAllDecksModal - Export all decks as MtgCraft Backup
 *
 * Exports all user decks as a JSON backup file that can be imported later.
 */
import { useMemo } from 'react';

import { useAtomValue } from 'jotai';
import { FaArchive } from 'react-icons/fa';

import Modal from '@/components/Modal/Modal';
import ExportPreview from '@/features/import-export/components/ExportPreview';
import { exportMultiDeckBackup } from '@/features/import-export/exporters/mtgcraft';
import type { Deck, DeckCard } from '@/types/deck';

import { allDeckCardsAtom } from '../state/allDeckCards';

// ============================================================================
// Types
// ============================================================================

interface ExportAllDecksModalProps {
  isOpen: boolean;
  onClose: () => void;
  decks: Deck[];
}

// ============================================================================
// Component
// ============================================================================

export default function ExportAllDecksModal({ isOpen, onClose, decks }: ExportAllDecksModalProps) {
  const allDeckCardsState = useAtomValue(allDeckCardsAtom);
  const allDeckCards = allDeckCardsState.data;

  // Group cards by deck
  const cardsByDeck = useMemo(() => {
    const map = new Map<string, DeckCard[]>();
    if (!allDeckCards) return map;

    for (const card of allDeckCards) {
      const deckCards = map.get(card.deck_id) || [];
      deckCards.push(card);
      map.set(card.deck_id, deckCards);
    }
    return map;
  }, [allDeckCards]);

  // Generate export content
  const exportContent = useMemo(() => {
    if (!isOpen || decks.length === 0) return '';

    const deckBackups = decks.map((deck) => {
      const deckCards = cardsByDeck.get(deck.id) || [];
      return { deck, cards: deckCards };
    });

    return exportMultiDeckBackup(deckBackups);
  }, [isOpen, decks, cardsByDeck]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(exportContent);
    return true;
  };

  const handleDownload = () => {
    const blob = new Blob([exportContent], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;

    // Generate filename: All Decks - MtgCraft - [timestamp].json
    const timestamp = new Date().toISOString().split('T')[0];
    a.download = `All Decks - MtgCraft - ${timestamp}.json`;

    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export All Decks">
      <div className="space-y-6 p-6">
        {/* Info */}
        <div className="flex items-start gap-3 rounded-lg bg-indigo-500/10 p-4">
          <FaArchive className="mt-0.5 flex-shrink-0 text-indigo-400" />
          <div className="text-sm text-zinc-300">
            <p className="font-medium">MtgCraft Backup Format</p>
            <p className="mt-1 text-zinc-400">
              Exporting {decks.length} deck{decks.length !== 1 ? 's' : ''} as a JSON backup file. This backup includes all deck metadata and
              card references.
            </p>
          </div>
        </div>

        {/* Preview */}
        <ExportPreview
          content={exportContent}
          onCopy={handleCopy}
          onDownload={handleDownload}
          label="Backup Preview"
          maxHeight="max-h-64"
        />
      </div>
    </Modal>
  );
}
